import React from "react";
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from "@mui/material";
import { CalendarMonth, TableRows, ViewWeek } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { dashboard2ToggleButtonGroupSx } from "../dashboard2-status.styles";

export enum Dashboard2ScheduleLayout {
  Week = "week",
  Calendar = "calendar",
  Table = "table"
}

type Dashboard2ScheduleLayoutToggleGroupProps = {
  layout: Dashboard2ScheduleLayout;
  onLayoutChange: (layout: Dashboard2ScheduleLayout) => void;
  /** Smaller icons — tier-3 chrome, not a page hero. Selected state still uses primary. */
  compact?: boolean;
  testIdSuffix?: string;
};

const COMPACT_ICON_SIZE = 20;
const DEFAULT_ICON_SIZE = 28;

export const Dashboard2ScheduleLayoutToggleGroup: React.FC<
  Dashboard2ScheduleLayoutToggleGroupProps
> = ({ layout, onLayoutChange, compact = false, testIdSuffix = "" }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const iconSize = compact ? COMPACT_ICON_SIZE : DEFAULT_ICON_SIZE;
  const suffix = testIdSuffix ? `-${testIdSuffix}` : "";

  return (
    <ToggleButtonGroup
      value={layout}
      exclusive
      size="small"
      color="primary"
      aria-label={t("_dashboard2_scheduleLayoutGroup")}
      onChange={(_, value: Dashboard2ScheduleLayout | null) => value && onLayoutChange(value)}
      data-testid={`dashboard2-schedule-layout-toggle-group${suffix}`}
      sx={dashboard2ToggleButtonGroupSx(theme, { compactTouchTargets: compact })}
    >
      <ToggleButton
        value={Dashboard2ScheduleLayout.Week}
        aria-label={t("_dashboard2_scheduleLayoutWeek")}
        data-testid={`dashboard2-schedule-layout-week${suffix}`}
      >
        <Tooltip title={<Typography>{t("_dashboard2_scheduleLayoutWeek")}</Typography>}>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
            <ViewWeek sx={{ fontSize: iconSize }} />
          </Box>
        </Tooltip>
      </ToggleButton>

      <ToggleButton
        value={Dashboard2ScheduleLayout.Calendar}
        aria-label={t("_dashboard2_scheduleLayoutCalendar")}
        data-testid={`dashboard2-schedule-layout-calendar${suffix}`}
      >
        <Tooltip title={<Typography>{t("_dashboard2_scheduleLayoutCalendar")}</Typography>}>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
            <CalendarMonth sx={{ fontSize: iconSize }} />
          </Box>
        </Tooltip>
      </ToggleButton>

      <ToggleButton
        value={Dashboard2ScheduleLayout.Table}
        aria-label={t("_dashboard2_scheduleLayoutTable")}
        data-testid={`dashboard2-schedule-layout-table${suffix}`}
      >
        <Tooltip title={<Typography>{t("_dashboard2_scheduleLayoutTable")}</Typography>}>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
            <TableRows sx={{ fontSize: iconSize }} />
          </Box>
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );
};
